import { action, computed, makeObservable, observable } from 'mobx';
import {
  ERROR_CODES,
  extractServerErrorCode,
  extractErrorKeyByErrorCode,
  isSendFailureCode,
} from '../utils/errorHandler';
import { RootStore } from './root';
import { AuthenticateByCodeStore } from './authenticateByCode';
import {
  loginTwoFactorAuthenticationVerifyClick,
  loginTwoFactorAuthenticationVerifySuccess,
  loginTwoFactorAuthenticationVerifyFail,
  loginTwoFactorAuthenticationResendCodeClick,
  loginTwoFactorAuthenticationLostPhoneAccessClick,
} from '@wix/bi-logger-hls2/v2';
import {
  NEW_ACCOUNT_RECOVERY_URL,
  getVerticalSupportUrl,
  TwoFactorAuthMethods,
  TwoFactorAuthPhoneDeliveryMethods,
  twoFAProcessMethod,
  TWOFA_PROCESS,
  TwoFactorAuthMethod,
  TwoFactorAuthPhoneDeliveryMethod,
  EXPERIMENTS,
  twoFactorDescriptionKeysMap,
  phoneDeliveryDescriptionKeysMap,
} from '../utils/constants';
import {
  shouldEnable2faLobby,
  isWhatsAppLobbyEnabled,
  shouldEnablePushRememberDevice,
} from './constants';
import { useContext } from 'react';
import { AppContextProvider } from '../components/AppLoader';
import {
  StateMachineResponse,
  StateType,
  FactorType,
  MfaReason,
  FactorStatus,
} from '@wix/ambassador-iam-authentication-v1-authentication/types';
import { ROUTES } from '../routes';
import { ApprovalChallengeStatus } from './premiumUsers2FaEnforcement/ownerApp/types';

const POLLING_INTERVAL = 2000;

type FactorMap = {
  [key in FactorType]: TwoFactorAuthMethod;
};
type MfaFactorMap = Omit<
  FactorMap,
  FactorType.PASSWORD | FactorType.UNKNOWN_FACTOR_TYPE | FactorType.CALL
>;
const factorMap: MfaFactorMap = {
  [FactorType.TOTP]: TwoFactorAuthMethods.TOTP,
  [FactorType.SMS]: TwoFactorAuthMethods.Phone,
  [FactorType.EMAIL]: TwoFactorAuthMethods.Email,
  [FactorType.PUSH]: TwoFactorAuthMethods.Push,
  [FactorType.WEBAUTHN]: TwoFactorAuthMethods.WebAuthn,
  [FactorType.RECOVERY_CODE]: TwoFactorAuthMethods.RecoveryCode,
};

export interface WebAuthn2FAChallengeData {
  challengeId?: string;
  publicKeyCredentialRequestOptionsJson?: string;
}

export type TwoFAActiveView = 'challenge' | 'twoFaLobby';

export interface TwoFaLobbyMethodDescriptor {
  key: string;
  method: TwoFactorAuthMethod;
  phoneDeliveryMethod?: TwoFactorAuthPhoneDeliveryMethod;
  labelKey: string;
}

export const getTwoFactorDescriptionKey = (
  method: TwoFactorAuthMethod,
  phoneDeliveryMethod?: string,
): string =>
  method === TwoFactorAuthMethods.Phone
    ? phoneDeliveryDescriptionKeysMap[
        (phoneDeliveryMethod as TwoFactorAuthPhoneDeliveryMethod) ??
          TwoFactorAuthPhoneDeliveryMethods.SMS
      ]
    : twoFactorDescriptionKeysMap[method];

export class TwoFactorAuthStore extends AuthenticateByCodeStore {
  twoFAToken: string;
  stateToken?: string;
  verificationId: string | undefined | null;
  uaToken: string | undefined;
  deviceName: string;
  userId: string;
  usingTwoFAFallback: boolean;
  allowRememberMe: boolean = true;
  rememberDevice: boolean = true;
  lfndForce2FA?: boolean;
  dialogTitleKey: string = 'enter_code.title';
  twoFactorAuthStatus: ApprovalChallengeStatus =
    ApprovalChallengeStatus.PENDING_APPROVAL;
  pollingTimer: NodeJS.Timeout | null = null;
  isFetchingStateToken: boolean = false;
  isCheckingStatusInProgress: boolean = false;
  phoneDeliveryMethod?: string;
  webauthnChallengeData?: WebAuthn2FAChallengeData;
  isLoading: boolean = false;
  activeView: TwoFAActiveView = 'challenge';
  // Send failure to surface inline under a specific lobby method's button when
  // the user is routed to the lobby (e.g. their SMS send failed). Uses the
  // existing per-method inline error, not a separate banner.
  lobbyMethodError?: { methodKey: string; errorCode: string };

  private urlParams: URLSearchParams = new URLSearchParams(
    window.location.search,
  );
  private isDebug: boolean = this.urlParams.get('debugCase') === 'mfa';
  private testPayload: Partial<TwoFactorAuthPayload> = {
    currentTwoFAMethod: this.urlParams.get('method'),
    enabledTwoFAMethods: this.urlParams.get('enabled')?.split(','),
    twoFAHint: this.urlParams.get('hint') ?? '',
  };

  constructor(rootStore: RootStore) {
    super(rootStore);
    this.SUBMIT_INTERACTION_NAME = 'verify-code-2fa';
    this.isDebug && this.setAuthParams(this.testPayload);
    makeObservable(this, {
      currentTwoFAMethod: observable,
      enabledTwoFAMethods: observable,
      twoFAHint: observable,
      twoFAToken: observable,
      usingTwoFAFallback: observable,
      twoFactorAuthStatus: observable,
      showMethodsSwitch: computed,
      showTwoFaLobbyEntry: computed,
      setAuthParams: action,
      verify: action,
      resendCode: action,
      onLostAccess: action.bound,
      stateToken: observable,
      phoneDeliveryMethod: observable,
      webauthnChallengeData: observable,
      isLoading: observable,
      verifyWithWebAuthn: action,
      activeView: observable,
      showTwoFaLobby: action.bound,
      rememberDevice: observable,
      setRememberDevice: action,
      lobbyMethodError: observable,
      setLobbyMethodError: action,
      handleResendSendFailure: action,
    });
  }

  getResendTimerInitialValue() {
    return this.rootStore.postLoginStore.shouldShowWixAppEnforcement ? 10 : 30;
  }

  /**
   * Opens the 2FA screen for a login that paused on its second factor: an IAM
   * MFA challenge, or the legacy 2FA payload.
   */
  startChallenge(
    payload: any,
    isIamChallenge = payload?.state === StateType.MFA_CHALLENGE,
  ) {
    this.rootStore.panorama.startTransaction(TWOFA_PROCESS);
    if (isIamChallenge) {
      this.setAuthParamsMfaIAM(payload);
    } else {
      this.setAuthParams(payload);
    }
    this.rootStore.navigationStore.navigate(ROUTES.TWO_FACTOR_AUTH);
  }

  setAuthParamsMfaIAM(payload: StateMachineResponse) {
    this.stateToken = payload.stateToken as string;
    const currentFactor = payload.mfaChallengeData?.factorType;
    const availableFactors = payload.mfaChallengeData?.availableFactors;
    if (
      this.rootStore.experiments.enabled(
        EXPERIMENTS.SHOULD_SUPPORT_FALLBACK_FACTOR,
      )
    ) {
      this.usingTwoFAFallback = this.calculateUsingTwoFAFallback(
        currentFactor,
        availableFactors,
      );
      this.enabledTwoFAMethods = this.calculateEnabledTwoFAMethods(
        availableFactors,
        this.currentTwoFAMethod,
      );
    } else {
      this.enabledTwoFAMethods =
        Array.isArray(availableFactors) && availableFactors.length > 0
          ? availableFactors.map((factor) =>
              factor.factorType
                ? factorMap[factor.factorType]
                : TwoFactorAuthMethods.Unused,
            )
          : [this.currentTwoFAMethod];
    }
    this.currentTwoFAMethod = currentFactor
      ? factorMap[currentFactor]
      : TwoFactorAuthMethods.Unused;
    this.rootStore.panorama.startTransaction(
      twoFAProcessMethod(this.currentTwoFAMethod),
    );
    this.twoFAToken = payload?.additionalData?.legacy2FAToken
      ?.strValue as string;
    this.twoFAHint = payload.mfaChallengeData?.verificationChallengeData?.hint;
    this.verificationId =
      payload.mfaChallengeData?.verificationChallengeData?.pushData?.transactionId;
    this.deviceName = this.getDeviceName(this.twoFAHint!);
    this.lfndForce2FA =
      payload.mfaChallengeData?.reason === MfaReason.HIGH_RISK_LOGIN;
  }

  private calculateUsingTwoFAFallback(currentFactor, availableFactors) {
    return (
      currentFactor === FactorType.EMAIL &&
      !!availableFactors?.some(
        (factor) =>
          factor.factorType === FactorType.SMS &&
          factor.factorStatus === FactorStatus.DISABLED_BY_RULE,
      )
    );
  }

  private calculateEnabledTwoFAMethods(availableFactors, currentMethod) {
    return Array.isArray(availableFactors) && availableFactors.length > 0
      ? availableFactors
          .filter(
            (factor) => factor.factorStatus !== FactorStatus.DISABLED_BY_RULE,
          )
          .map((factor) =>
            factor.factorType
              ? factorMap[factor.factorType]
              : TwoFactorAuthMethods.Unused,
          )
      : [currentMethod];
  }

  setAuthParams(payload: Partial<TwoFactorAuthPayload>) {
    if (payload.stateToken) {
      this.stateToken = payload.stateToken;
    }
    this.twoFAHint = payload.twoFAHint!;
    this.twoFAToken = payload.twoFAToken!;
    this.usingTwoFAFallback = this.isDebug
      ? false
      : !!payload.usingTwoFAFallback;
    if (this.isDebug) {
      this.currentTwoFAMethod = this.urlParams.get('method') as any;
    } else {
      this.currentTwoFAMethod =
        this.usingTwoFAFallback || !payload.currentTwoFAMethod
          ? TwoFactorAuthMethods.Email
          : payload.currentTwoFAMethod;
    }
    this.rootStore.panorama.startTransaction(
      twoFAProcessMethod(this.currentTwoFAMethod),
    );
    this.enabledTwoFAMethods = payload.enabledTwoFAMethods ?? [
      this.currentTwoFAMethod,
    ];
    this.verificationId = payload.verificationId;
    this.uaToken = payload.uaToken;
    this.deviceName = this.getDeviceName(payload.twoFAHint!);
    this.lfndForce2FA = payload.lfndForce2FA;
  }

  getDeviceName(twoFAHint: string) {
    try {
      const { deviceBrandName, deviceModelName } = JSON.parse(twoFAHint);
      return `${deviceBrandName} ${deviceModelName}`;
    } catch (error) {
      return '';
    }
  }

  onResendCodeClicked(
    options: { phoneDeliveryMethod?: string } = {
      phoneDeliveryMethod: TwoFactorAuthPhoneDeliveryMethods.SMS,
    },
  ) {
    return this.resendCode(options);
  }

  getResendButtonKey() {
    if (this.twoFactorAuthStatus === ApprovalChallengeStatus.PENDING_APPROVAL) {
      return 'twoFactorAuth.resendNotification';
    }

    return 'twoFactorAuth.resendLoginRequest';
  }

  waitForStateToken = async () => {
    return new Promise<void>((resolve) => {
      const interval = setInterval(() => {
        if (!this.isFetchingStateToken) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
    });
  };

  async resendCode(
    options: { phoneDeliveryMethod?: string } = {
      phoneDeliveryMethod: TwoFactorAuthPhoneDeliveryMethods.SMS,
    },
  ) {
    const { phoneDeliveryMethod } = options;
    this.phoneDeliveryMethod = phoneDeliveryMethod;

    this.rootStore.biLogger.report(
      loginTwoFactorAuthenticationResendCodeClick({
        flow_type: this.currentTwoFAMethod,
        tfa_type: 'login',
        reason: this.lfndForce2FA ? 'LFND' : 'login',
        // delivery_method only applies to the Phone factor; empty for Email etc.
        delivery_method:
          this.currentTwoFAMethod === TwoFactorAuthMethods.Phone
            ? phoneDeliveryMethod
            : undefined,
      }),
    );

    try {
      if (
        this.rootStore.experiments.enabled(EXPERIMENTS.USE_IAM_FOR_PUSH_FACTOR)
      ) {
        await this.waitForStateToken();
      }
      const data = await this.rootStore.apiStore.resendSecondFactorCode({
        twoFAToken: this.twoFAToken,
        twoFAMethod: this.currentTwoFAMethod,
        phoneDeliveryMethod,
        stateToken: this.stateToken,
      });

      if (data?.payload?.stateToken) {
        this.stateToken = data.payload?.stateToken;
      }

      if (data?.payload?.currentTwoFAMethod === TwoFactorAuthMethods.Push) {
        this.setAuthParams(data.payload);
        this.setTwoFactorAuthStatus(ApprovalChallengeStatus.PENDING_APPROVAL);
        this.startPolling();
      }

      if (data?.payload?.twoFAHint) {
        this.twoFAHint = data.payload?.twoFAHint;
      }

      if (data?.payload?.twoFAToken) {
        this.twoFAToken = data?.payload?.twoFAToken ?? '';
      } else {
        return extractServerErrorCode(data);
      }
    } catch (error: any) {
      return extractServerErrorCode(error);
    }
  }

  /**
   * Fetch challenge data for a 2FA method.
   * Updates stateToken and twoFAToken in the store (server may invalidate old tokens).
   * Returns the full payload for the caller to use as needed.
   * @throws {Error} with error code as message on failure
   */
  private async fetchMethodChallenge(
    method: TwoFactorAuthMethod,
    phoneDeliveryMethod?: string,
  ): Promise<{
    stateToken?: string;
    twoFAToken: string;
    twoFAHint?: string;
    webauthnChallengeData?: WebAuthn2FAChallengeData;
  }> {
    if (
      this.rootStore.experiments.enabled(EXPERIMENTS.USE_IAM_FOR_PUSH_FACTOR)
    ) {
      await this.waitForStateToken();
    }

    let data;
    try {
      data = await this.rootStore.apiStore.resendSecondFactorCode({
        twoFAToken: this.twoFAToken,
        twoFAMethod: method,
        phoneDeliveryMethod,
        stateToken: this.stateToken,
      });
    } catch (error: any) {
      throw new Error(extractServerErrorCode(error));
    }

    if (!data?.payload?.twoFAToken) {
      throw new Error(extractServerErrorCode(data));
    }

    // Always update tokens - server may invalidate old ones
    if (data.payload.stateToken) {
      this.stateToken = data.payload.stateToken;
    }
    if (data.payload.verificationId) {
      this.verificationId = data.payload.verificationId;
    }
    this.twoFAToken = data.payload.twoFAToken;

    return {
      stateToken: data.payload.stateToken,
      twoFAToken: data.payload.twoFAToken,
      twoFAHint: data.payload.twoFAHint,
      webauthnChallengeData: data.payload.webauthnChallengeData,
    };
  }

  /**
   * Atomically switch to a new 2FA method.
   * Makes the API call FIRST, then updates state only on success.
   * This prevents the UI from showing stale/empty hints during the transition.
   * @throws {Error} with error code as message on failure
   */
  async switchToMethod(
    newMethod: TwoFactorAuthMethod,
    options: { phoneDeliveryMethod?: string } = {},
  ): Promise<void> {
    const { phoneDeliveryMethod } = options;

    // fetchMethodChallenge updates tokens, we update the rest
    const challengeData = await this.fetchMethodChallenge(
      newMethod,
      phoneDeliveryMethod,
    );

    this.currentTwoFAMethod = newMethod;
    this.phoneDeliveryMethod = phoneDeliveryMethod;

    if (challengeData.twoFAHint) {
      this.twoFAHint = challengeData.twoFAHint;
    }
    if (challengeData.webauthnChallengeData) {
      this.webauthnChallengeData = challengeData.webauthnChallengeData;
    }

    this.activeView = 'challenge';
    this.lobbyMethodError = undefined;
  }

  backToLogin() {
    this.rootStore.navigationStore.goToMainLogin({
      email: this.rootStore.userDataStore.email,
    });
  }

  resendOwnerAppNotification = async () => {
    if (this.twoFactorAuthStatus === ApprovalChallengeStatus.EXPIRED) {
      this.twoFactorAuthStatus = ApprovalChallengeStatus.PENDING_APPROVAL;
    }
    if (
      this.rootStore.experiments.enabled(EXPERIMENTS.USE_IAM_FOR_PUSH_FACTOR)
    ) {
      this.stopPolling();
      await this.waitForStateToken();
    }
    const { payload } = await this.rootStore.apiStore.resendSecondFactorCode({
      twoFAToken: this.twoFAToken,
      twoFAMethod: this.currentTwoFAMethod,
      stateToken: this.stateToken,
    });
    if (payload?.stateToken) {
      this.stateToken = payload?.stateToken;
    }
    this.setAuthParams(payload);
    this.startPolling();
  };

  verify({ code, rememberMe }: { code: string; rememberMe?: boolean }) {
    this.rootStore.biLogger.report(
      loginTwoFactorAuthenticationVerifyClick({
        remember_this_device: rememberMe,
        flow_type: this.currentTwoFAMethod,
        tfa_type: 'login',
        view: 'login',
        delivery_method: this.phoneDeliveryMethod,
      }),
    );
    this.rootStore.panorama.startTransaction(this.SUBMIT_INTERACTION_NAME);
    return this.rootStore.apiStore
      .verifyCodeAndLogin({
        stateToken: this.stateToken,
        twoFAMethod: this.currentTwoFAMethod,
        twoFAToken: this.twoFAToken,
        code,
        rememberMe,
        phoneDeliveryMethod: this.phoneDeliveryMethod,
      })
      .then((data) => {
        if (data.success) {
          this.rootStore.panorama.finishTransaction(
            this.SUBMIT_INTERACTION_NAME,
          );
          this.rootStore.panorama.finishTransaction(TWOFA_PROCESS);
          this.rootStore.panorama.finishTransaction(
            twoFAProcessMethod(this.currentTwoFAMethod),
          );
          this.rootStore.biLogger.report(
            loginTwoFactorAuthenticationVerifySuccess({
              flow_type: this.currentTwoFAMethod,
              reason: this.lfndForce2FA ? 'LFND' : 'login',
              view: 'login',
              delivery_method: this.phoneDeliveryMethod,
            }),
          );
          const finalData = data?.payload ?? data?.data;
          this.rootStore.navigationStore.postLogin(finalData);
          return;
        }
        this.rootStore.biLogger.report(
          loginTwoFactorAuthenticationVerifyFail({
            flow_type: this.currentTwoFAMethod,
            reason: this.lfndForce2FA ? 'LFND' : 'login',
            view: 'login',
            delivery_method: this.phoneDeliveryMethod,
          }),
        );
        const errorCode = extractServerErrorCode(data);
        this.handleError(errorCode);
        throw { errorCode };
      })
      .catch((error) => {
        // IAM verify rejects on failure (legacy resolves success:false and
        // fires VerifyFail above). The success===false guard fires it once for
        // IAM failures without double-firing legacy or catching network errors.
        if (error?.success === false) {
          this.rootStore.biLogger.report(
            loginTwoFactorAuthenticationVerifyFail({
              flow_type: this.currentTwoFAMethod,
              reason: this.lfndForce2FA ? 'LFND' : 'login',
              view: 'login',
              delivery_method: this.phoneDeliveryMethod,
            }),
          );
        }
        if (error?.errorCode === ERROR_CODES.VALIDATION_ERROR_TOTP_IAM) {
          this.handleError(error.errorData?.fieldViolations[0]?.ruleName);
        }
        if (error?.errorData === ERROR_CODES.TOKEN_EXPIRED_IAM) {
          this.handleError(error.errorData);
        }
        if (!error?.errorCode) {
          this.handleError(ERROR_CODES.GENERAL_ERROR_CODE);
        }
        this.handleError(error.errorCode);
        throw new Error(error);
      });
  }

  private showToastError(errorCode: string): void {
    const errorKey = extractErrorKeyByErrorCode(errorCode);
    if (errorKey) {
      this.rootStore.notificationStore.openErrorToast(
        this.rootStore.i18n.t(errorKey),
      );
    }
  }

  private extractPasskeyErrorCode(error: unknown): string {
    const errorCode =
      error instanceof Error ? error.message : extractServerErrorCode(error);
    const hasKnownErrorKey = !!extractErrorKeyByErrorCode(errorCode, {
      defaultError: '',
    });

    if (errorCode !== ERROR_CODES.GENERAL_ERROR_CODE && hasKnownErrorKey) {
      return errorCode;
    }

    // Keep parity with the main login passkey flow for generic/unknown errors.
    return ERROR_CODES.TECHNICAL_ERROR;
  }

  /**
   * Submit WebAuthn credential to server.
   * Manages its own Panorama transaction lifecycle.
   * @returns login data on success, null on failure (error already handled)
   */
  private async submitWebAuthnCredential(
    webauthnData: { challengeId: string; credential: string },
    twoFAMethod: TwoFactorAuthMethod,
    rememberMe?: boolean,
  ): Promise<any | null> {
    this.rootStore.panorama.startTransaction(this.SUBMIT_INTERACTION_NAME);

    try {
      const data = await this.rootStore.apiStore.verifyCodeAndLogin({
        stateToken: this.stateToken,
        twoFAMethod,
        twoFAToken: this.twoFAToken,
        rememberMe,
        webauthnData,
      });

      if (data.success) {
        this.rootStore.panorama.finishTransaction(this.SUBMIT_INTERACTION_NAME);
        return data?.payload ?? data?.data;
      }

      throw new Error(extractServerErrorCode(data));
    } catch (error) {
      this.rootStore.panorama.finishTransaction(this.SUBMIT_INTERACTION_NAME);
      throw error;
    }
  }

  /**
   * Complete 2FA WebAuthn/passkey verification flow.
   * Gets challenge, authenticates with browser, and completes verification.
   * Does NOT change the current 2FA method - passkey runs independently.
   * Shows error notification on failure.
   */
  async verifyWithWebAuthn(rememberMe?: boolean): Promise<void> {
    // Prevent multiple simultaneous WebAuthn verification attempts
    if (this.isLoading) {
      return;
    }

    const apiTwoFAMethod = TwoFactorAuthMethods.WebAuthn;
    const biFlowType = 'Passkey';

    const reportStart = () => {
      this.rootStore.biLogger.report(
        loginTwoFactorAuthenticationVerifyClick({
          remember_this_device: rememberMe,
          flow_type: biFlowType,
          tfa_type: 'login',
          view: 'login',
        }),
      );
    };

    const reportSuccess = () => {
      this.rootStore.panorama.finishTransaction(TWOFA_PROCESS);
      this.rootStore.panorama.finishTransaction(
        twoFAProcessMethod(apiTwoFAMethod),
      );
      this.rootStore.biLogger.report(
        loginTwoFactorAuthenticationVerifySuccess({
          flow_type: biFlowType,
          reason: this.lfndForce2FA ? 'LFND' : 'login',
          view: 'login',
        }),
      );
    };

    const reportError = (errorCode: string) => {
      this.rootStore.biLogger.report(
        loginTwoFactorAuthenticationVerifyFail({
          flow_type: biFlowType,
          reason: this.lfndForce2FA ? 'LFND' : 'login',
          view: 'login',
        }),
      );
      this.showToastError(errorCode);
    };

    // Main flow
    reportStart();
    this.rootStore.notificationStore.close();
    this.isLoading = true;

    try {
      // 1. Fetch WebAuthn challenge (also updates tokens in store)
      const challengeData = await this.fetchMethodChallenge(apiTwoFAMethod);
      if (!challengeData.webauthnChallengeData) {
        reportError(ERROR_CODES.WEBAUTHN_CHALLENGE_FAILED);
        return;
      }

      // 2. Authenticate with browser
      const result =
        await this.rootStore.webauthnStore.authenticateWith2FAChallenge(
          challengeData.webauthnChallengeData,
        );
      if (result.status === 'cancelled') {
        return;
      }
      if (result.status === 'error') {
        reportError(result.errorCode);
        return;
      }

      // 3. Submit credential to server
      const loginData = await this.submitWebAuthnCredential(
        result.data,
        apiTwoFAMethod,
        rememberMe,
      );

      reportSuccess();
      this.rootStore.navigationStore.postLogin(loginData);
    } catch (error) {
      reportError(this.extractPasskeyErrorCode(error));
    } finally {
      this.isLoading = false;
    }
  }

  showResendButton() {
    return this.currentTwoFAMethod !== TwoFactorAuthMethods.TOTP;
  }

  getTitleKey() {
    if (this.lfndForce2FA) {
      return 'enter_code.lfnd.title';
    }
    return this.usingTwoFAFallback
      ? 'enter_code.fallback.title'
      : 'enter_code.title';
  }

  getDescriptionKey() {
    if (this.lfndForce2FA) {
      return 'enter_code.lfnd.description';
    }
    if (this.usingTwoFAFallback) {
      return 'enter_code.fallback.description';
    }
    // Per-delivery-channel descriptions (SMS / voice / WhatsApp) are a
    // Lobby-only feature. With the experiment off, keep the original
    // per-method description so existing flows (incl. voice call) are unchanged.
    if (!this.isTwoFaLobbyEnabled) {
      return twoFactorDescriptionKeysMap[this.currentTwoFAMethod];
    }
    return getTwoFactorDescriptionKey(
      this.currentTwoFAMethod,
      this.phoneDeliveryMethod,
    );
  }

  enableAccess() {
    return !!(this.twoFAToken && this.currentTwoFAMethod) || this.isDebug;
  }

  onLostAccess() {
    this.rootStore.biLogger.report(
      loginTwoFactorAuthenticationLostPhoneAccessClick({
        flow_type: this.currentTwoFAMethod,
      }),
    );
    // 2FA lockout is the one recovery that strands these users: the pre-screen
    // leads to the questionnaire, which verifies people by the published Wix
    // site they own — and they own none, so it rejects them at the end. Their
    // own support is the only route back in.
    const supportUrl = getVerticalSupportUrl(this.rootStore.userType);
    if (supportUrl) {
      this.rootStore.navigationStore.redirect(supportUrl);
      return;
    }

    const redirectUrl = `${NEW_ACCOUNT_RECOVERY_URL}/two-fa-pre-screen?${new URLSearchParams(
      {
        from: window.location.href,
        sessionId: this.rootStore.sessionId.get(),
        email: this.rootStore.userDataStore.email,
      },
    ).toString()}`;
    this.rootStore.navigationStore.redirect(redirectUrl);
  }

  public async getApprovalChallengeStatus(): Promise<
    ApprovalChallengeStatus | undefined
  > {
    try {
      if (!this.verificationId) {
        return undefined;
      }
      this.isFetchingStateToken = true;
      const { response, stateToken } =
        await this.rootStore.apiStore.getChallenge(
          this.verificationId,
          this.stateToken,
          this.pushRememberThisDevice,
        );
      if (stateToken) {
        this.stateToken = stateToken;
      }
      return response?.challenge?.approvalChallenge?.status;
    } catch (error) {
      return undefined;
    } finally {
      this.isFetchingStateToken = false;
    }
  }

  get showMethodsSwitch() {
    return !this.usingTwoFAFallback && this.enabledTwoFAMethods?.length! > 1;
  }

  // The Lobby entry ("Verify Another Way") gates on the number of Lobby ROWS,
  // not the number of factors. A phone-only user has a single factor but
  // multiple rows (SMS / voice / WhatsApp), and must still be able to open the
  // Lobby to switch delivery channel.
  get showTwoFaLobbyEntry() {
    return !this.usingTwoFAFallback && this.twoFaLobbyMethods.length > 1;
  }

  get isTwoFaLobbyEnabled() {
    // Gated by the FT (master switch) and scoped to the Wix 2025 / Wixel
    // brands — other presets keep the classic 2FA dialog for now.
    const { isWix2025, isWixel } = this.rootStore.displayStore;
    return (
      shouldEnable2faLobby(this.rootStore.experiments) && (isWix2025 || isWixel)
    );
  }

  showTwoFaLobby() {
    this.activeView = 'twoFaLobby';
  }

  setLobbyMethodError(value?: { methodKey: string; errorCode: string }) {
    this.lobbyMethodError = value;
  }

  // The lobby row (twoFaLobbyMethods key) for the method whose send just failed,
  // so the error can be shown under that button using the existing inline style.
  private failedLobbyMethodKey(
    phoneDeliveryMethod?: string,
  ): string | undefined {
    switch (this.currentTwoFAMethod) {
      case TwoFactorAuthMethods.Email:
        return 'email';
      case TwoFactorAuthMethods.TOTP:
        return 'totp';
      case TwoFactorAuthMethods.Push:
        return 'push';
      case TwoFactorAuthMethods.Phone:
        if (
          phoneDeliveryMethod === TwoFactorAuthPhoneDeliveryMethods.VOICE_CALL
        ) {
          return 'phone_call';
        }
        if (
          phoneDeliveryMethod === TwoFactorAuthPhoneDeliveryMethods.WHATSAPP
        ) {
          return 'phone_whatsapp';
        }
        return 'phone';
      default:
        return undefined;
    }
  }

  // Maps a send-failure code (+ delivery method) to the channel used in the
  // retry copy. FACTOR_OPERATION_FAILED is generic, so fall back to the current
  // method to still name the channel the user just tried.
  private sendFailureChannel(
    errorCode: string,
    phoneDeliveryMethod?: string,
  ): 'sms' | 'voice_call' | 'email' | 'generic' {
    switch (errorCode) {
      case ERROR_CODES.SMS_SEND_FAILED:
        return 'sms';
      case ERROR_CODES.VOICE_CALL_SEND_FAILED:
        return 'voice_call';
      case ERROR_CODES.EMAIL_SEND_FAILED:
        return 'email';
      default:
        if (this.currentTwoFAMethod === TwoFactorAuthMethods.Email) {
          return 'email';
        }
        if (this.currentTwoFAMethod === TwoFactorAuthMethods.Phone) {
          return phoneDeliveryMethod ===
            TwoFactorAuthPhoneDeliveryMethods.VOICE_CALL
            ? 'voice_call'
            : 'sms';
        }
        return 'generic';
    }
  }

  // On a resend send failure: when another method exists (and the lobby can
  // render), route to the lobby and surface the failure through the existing
  // inline-under-the-button error (no separate banner, no retry CTA — the picker
  // is the next step). Otherwise show a retry/come-back-later toast. Returns true
  // when handled so the caller skips its own error surface.
  handleResendSendFailure(
    errorCode: string,
    phoneDeliveryMethod?: string,
  ): boolean {
    if (!isSendFailureCode(errorCode)) {
      return false;
    }
    const methodKey = this.failedLobbyMethodKey(phoneDeliveryMethod);
    if (this.isTwoFaLobbyEnabled && this.showTwoFaLobbyEntry && methodKey) {
      this.setLobbyMethodError({ methodKey, errorCode });
      this.showTwoFaLobby();
    } else {
      const channel = this.sendFailureChannel(errorCode, phoneDeliveryMethod);
      this.rootStore.notificationStore.openErrorToast(
        this.rootStore.i18n.t(`enter_code.send_failed.retry.${channel}`),
      );
    }
    return true;
  }

  get twoFaLobbyMethods(): TwoFaLobbyMethodDescriptor[] {
    const rows: TwoFaLobbyMethodDescriptor[] = [];
    for (const method of this.enabledTwoFAMethods ?? []) {
      switch (method) {
        case TwoFactorAuthMethods.TOTP:
          rows.push({ key: 'totp', method, labelKey: 'enter_code.lobby.totp' });
          break;
        case TwoFactorAuthMethods.Push:
          rows.push({ key: 'push', method, labelKey: 'enter_code.lobby.push' });
          break;
        case TwoFactorAuthMethods.Email:
          rows.push({
            key: 'email',
            method,
            labelKey: 'enter_code.lobby.email',
          });
          break;
        case TwoFactorAuthMethods.Phone:
          if (
            isWhatsAppLobbyEnabled(this.rootStore.experiments) &&
            this.stateToken !== undefined
          ) {
            rows.push({
              key: 'phone_whatsapp',
              method,
              phoneDeliveryMethod: TwoFactorAuthPhoneDeliveryMethods.WHATSAPP,
              labelKey: 'enter_code.lobby.whatsapp',
            });
          }
          rows.push({
            key: 'phone',
            method,
            phoneDeliveryMethod: TwoFactorAuthPhoneDeliveryMethods.SMS,
            labelKey: 'enter_code.lobby.phone',
          });
          rows.push({
            key: 'phone_call',
            method,
            phoneDeliveryMethod: TwoFactorAuthPhoneDeliveryMethods.VOICE_CALL,
            labelKey: 'enter_code.lobby.phone_call',
          });
          break;
        // WebAuthn (passkey) and RecoveryCode aren't offered as selectable 2FA
        // login methods, so they intentionally don't get a Lobby row.
        default:
          break;
      }
    }
    return rows;
  }

  setTwoFactorAuthStatus(status: ApprovalChallengeStatus) {
    this.twoFactorAuthStatus = status;
  }

  setRememberDevice(value: boolean) {
    this.rememberDevice = value;
  }

  private get pushRememberThisDevice(): boolean {
    return shouldEnablePushRememberDevice(this.rootStore.experiments)
      ? this.rememberDevice
      : false;
  }

  async checkStatus() {
    const verificationStatus = await this.getApprovalChallengeStatus();
    if (verificationStatus === ApprovalChallengeStatus.APPROVED) {
      this.stopPolling();
      let shouldCallVerify = true;
      let verificationSucceeded = true;
      let uaToken;
      if (
        this.currentTwoFAMethod === TwoFactorAuthMethods.Push &&
        this.stateToken !== undefined &&
        this.rootStore.experiments.enabled(EXPERIMENTS.USE_IAM_FOR_PUSH_FACTOR)
      ) {
        shouldCallVerify = false;
      }
      if (shouldCallVerify) {
        const { payload, success } =
          await this.rootStore.apiStore.verifyCodeAndLogin({
            stateToken: this.stateToken,
            twoFAMethod: this.currentTwoFAMethod,
            twoFAToken: this.twoFAToken,
            rememberMe: this.pushRememberThisDevice,
          });
        uaToken = payload.uaToken;
        verificationSucceeded = success;
      }
      if (verificationSucceeded) {
        this.rootStore.panorama.finishTransaction(TWOFA_PROCESS);
        this.rootStore.panorama.finishTransaction(
          twoFAProcessMethod(this.currentTwoFAMethod),
        );
      }
      await this.rootStore.navigationStore.postLogin({
        uaToken,
      });
    }
    if (verificationStatus === ApprovalChallengeStatus.EXPIRED) {
      this.setTwoFactorAuthStatus(ApprovalChallengeStatus.EXPIRED);
      this.stopPolling();
    }
    if (verificationStatus === ApprovalChallengeStatus.DECLINED) {
      this.setTwoFactorAuthStatus(ApprovalChallengeStatus.DECLINED);
      this.stopPolling();
    }
  }

  private scheduleNextPoll() {
    this.pollingTimer = setTimeout(async () => {
      if (!this.isCheckingStatusInProgress) {
        this.isCheckingStatusInProgress = true;
        await this.checkStatus();
        this.isCheckingStatusInProgress = false;
      }
      // Only schedule next poll if we haven't been stopped
      if (this.pollingTimer !== null) {
        this.scheduleNextPoll();
      }
    }, POLLING_INTERVAL);
  }

  public startPolling() {
    this.stopPolling();
    this.scheduleNextPoll();
  }

  public stopPolling() {
    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
  }
}

export const useTwoFactorAuth = () => {
  const {
    rootStore: { twoFactorAuthStore, biLogger },
  } = useContext(AppContextProvider);

  return { store: twoFactorAuthStore, biLogger };
};
